/**
 * One home for how a compliance position is *read*.
 *
 * The stored enum has five values but seven display states: `unassessed` means
 * "nobody has looked", "the assessor looked and abstained", or "the assessor
 * never searched the library for it by design" (pricing, declarations — mig
 * 180), and those demand different next actions (spec §5). That split, plus
 * the definition of "at risk", was written twice before Phase 2 and is now
 * consumed by five surfaces — the matrix, the pill, the Go/No-Go banner, the
 * decision dialog and the Overview tile. Keeping it in one place is what stops
 * the mandatory call meaning different things on different screens.
 */

import type {
  ExtractedComplianceItem,
  PositionValue,
  UnassessedReason,
} from '@/types/extraction'

export type PositionDisplayState =
  | 'comply'
  | 'partial'
  | 'non_comply'
  | 'not_applicable'
  | 'needs_review'
  | 'needs_confirmation'
  | 'not_assessed'

export type PositionBearing = Pick<
  ExtractedComplianceItem,
  'position' | 'positionAssessedAt' | 'unassessedReason'
>
export type RiskBearing = PositionBearing &
  Pick<ExtractedComplianceItem, 'criticality' | 'responseExpectation'>

/**
 * The stamp decides first: a row with no assessed stamp is "not assessed"
 * whatever reason it once carried. Only an assessed `unassessed` row reads its
 * reason, and a decided position never does.
 */
export function positionDisplayState(
  position: PositionValue | undefined,
  assessedAt: string | null | undefined,
  unassessedReason?: UnassessedReason | null
): PositionDisplayState {
  const value = position ?? 'unassessed'
  if (value !== 'unassessed') return value
  if (assessedAt == null) return 'not_assessed'
  return unassessedReason === 'not_searched' ? 'needs_confirmation' : 'needs_review'
}

export function displayStateOf(item: PositionBearing): PositionDisplayState {
  return positionDisplayState(item.position, item.positionAssessedAt, item.unassessedReason)
}

/**
 * Assessed, but still no verdict: the assessor looked and abstained, or it
 * never searched because only the bid manager can confirm the requirement.
 * Two labels, one kind of risk. On a mandatory row they are counted together
 * under "needs review", with "Not assessed" ({@link MANDATORY_UNRESOLVED_STATES}).
 */
export const ASSESSED_UNRESOLVED_STATES: readonly ['needs_review', 'needs_confirmation'] = [
  'needs_review',
  'needs_confirmation',
]

/**
 * The display states that count as "at risk" on ANY row: a requirement we
 * either fail, or cannot vouch for after assessing it. A non-mandatory row
 * nobody has looked at is not at risk — it is just not looked at yet.
 */
export const AT_RISK_STATES: readonly ['non_comply', 'needs_review', 'needs_confirmation'] = [
  'non_comply',
  ...ASSESSED_UNRESOLVED_STATES,
]

/**
 * A MANDATORY row is at risk unless its position is comply or N/A (partial is
 * the one deliberate exception — it is counted on its own, as a departure, not
 * as at risk). So on top of the assessed-unresolved labels, a mandatory row
 * that reads "Not assessed" counts too — whether nobody ever assessed it, or a
 * person cleared it back. Before 2026-09-25 it did not, and clearing a verdict
 * shrank the "mandatory at risk" number.
 *
 * These are the rows the banner and dialogs call "needs review", and what their
 * deep links carry, so the linked table shows every row the count names.
 */
export const MANDATORY_UNRESOLVED_STATES: readonly [
  'needs_review',
  'needs_confirmation',
  'not_assessed',
] = [...ASSESSED_UNRESOLVED_STATES, 'not_assessed']

/**
 * The display states that make a mandatory row "at risk". The matrix's
 * "N mandatory at risk" chip filters to exactly these (with criticality
 * mandatory), and `?position=at_risk&criticality=mandatory` expands to them —
 * the definition must not be restated per surface.
 */
export const MANDATORY_AT_RISK_STATES: readonly [
  'non_comply',
  'needs_review',
  'needs_confirmation',
  'not_assessed',
] = ['non_comply', ...MANDATORY_UNRESOLVED_STATES]

/**
 * A requirement we either fail, or cannot vouch for; for a mandatory row, also
 * one nobody has vouched for at all (see {@link MANDATORY_AT_RISK_STATES}).
 */
export function isAtRisk(item: PositionBearing & Pick<ExtractedComplianceItem, 'criticality'>): boolean {
  const state = displayStateOf(item)
  const states: readonly PositionDisplayState[] =
    item.criticality === 'mandatory' ? MANDATORY_AT_RISK_STATES : AT_RISK_STATES
  return states.includes(state)
}

/**
 * Post-award obligations carry positions like everything else, but they are not
 * part of what gets submitted — so they are excluded from every count and every
 * warning. `responseExpectation` defaults to 'narrative' exactly as the matrix
 * does.
 */
export function submissionRelevant<
  T extends Pick<ExtractedComplianceItem, 'responseExpectation'>,
>(items: T[]): T[] {
  return items.filter((item) => (item.responseExpectation ?? 'narrative') !== 'post_award')
}

/**
 * `needsReview` spans every unresolved label ("Needs review", "Needs your
 * confirmation" and "Not assessed" — {@link MANDATORY_UNRESOLVED_STATES}), so
 * `nonComply + needsReview` is every at-risk row, and a "N mandatory needs
 * review" link must carry all three states.
 */
export function mandatoryAtRiskItems<T extends RiskBearing>(
  items: T[]
): { nonComply: T[]; needsReview: T[] } {
  const mandatory = submissionRelevant(items).filter((item) => item.criticality === 'mandatory')
  const unresolved: readonly PositionDisplayState[] = MANDATORY_UNRESOLVED_STATES
  return {
    nonComply: mandatory.filter((item) => displayStateOf(item) === 'non_comply'),
    needsReview: mandatory.filter((item) => unresolved.includes(displayStateOf(item))),
  }
}

/**
 * `needsConfirmation` is the part of `needsReview` that reads "Needs your
 * confirmation" — a subset, never added on top: `atRisk` is still
 * `nonComply + needsReview`.
 */
export function countMandatoryRisk(items: RiskBearing[]): {
  nonComply: number
  needsReview: number
  needsConfirmation: number
  atRisk: number
} {
  const { nonComply, needsReview } = mandatoryAtRiskItems(items)
  return {
    nonComply: nonComply.length,
    needsReview: needsReview.length,
    needsConfirmation: needsReview.filter(
      (item) => displayStateOf(item) === 'needs_confirmation'
    ).length,
    atRisk: nonComply.length + needsReview.length,
  }
}

/**
 * The words after a "needs review" count (the banner and both dialogs). A count
 * that includes a "Needs your confirmation" row says so — "need review or your
 * confirmation", singular "needs …" for one. Otherwise each surface keeps the
 * wording it has always had (`unchanged`), so a bid with no such row reads as
 * before.
 */
export function reviewCountPhrase(
  count: number,
  confirmationCount: number,
  unchanged: string
): string {
  if (confirmationCount === 0) return unchanged
  return count === 1 ? 'needs review or your confirmation' : 'need review or your confirmation'
}

/**
 * Deep link into the compliance matrix. `position` accepts display vocabulary
 * (`at_risk`, `needs_review`, `needs_confirmation`, `not_assessed`) as well as
 * stored enum values, alone or as a comma list — the matrix's URL parser maps
 * them (spec §5 rule 1).
 */
export function complianceMatrixLink(
  bidId: string,
  params?: { position?: string; criticality?: string; source?: 'ai' }
): string {
  const search = new URLSearchParams({ view: 'compliance_matrix' })
  if (params?.position) search.set('position', params.position)
  if (params?.criticality) search.set('criticality', params.criticality)
  if (params?.source) search.set('source', params.source)
  return `/app/bids/${bidId}/analysis?${search.toString()}`
}
